import React, { Component, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import PropTypes from 'prop-types';
import { AA_PREFERENCES } from './ConstPreferences';
import Body from '../Components/layout/Body';
import InfoMessage from '../Components/layout/InfoMessage';

const VISUAL_BKGND_HELP = `
  <div>
    <p><strong>Purpose: </strong>It's important to ask about visual aids and strategies, as sometimes clients are unable to initiate 
    requesting such items, or don't recall that they need such items to have functional vision.</p>

    <p>Eyeglasses are a common visual aid, but keep in mind that the prescription might not be up-to-date, or may not actually improve vision for computer / device use.
    Other aids and strategies might include:
      <ul>
        <li>High contrast</li>
        <li>Functional color preference</li>
        <li>Functional visual fields</li>
        <li>Spot patching</li>
        <li>Lighting needs</li>
        <li>Magnification use</li>
        <li>CCTV</li>
        <li>Guide lines</li>
      </ul>
    </p>
    <p>Regarding a formal vision assessment, you could ask when the client last had one.  
    If you have information from prior assessment, or self-report from the client, consider key points such as:
      <ul>
        <li>Diagnosis of Cortical Vision Impairment (CVI), along with CVI status such as color preference, functional visual fields, highlighting, underlining, etc.</li>
        <li>Ocular motor impairment or cranial nerve III palsy</li>
        <li>History of glaucoma, macular degeneration, diabetic retinopathy, cataracts</li>
        <li>Eye injuries</li>
        <li>Active infection</li>
      </ul>
    </p>
    <p><strong>Note: </strong>If this background info is difficult to obtain, you may want to observe even more closely than usual during this eval to notice visual issues that may cause challenges for the client.</p>
  </div>
`

const propTypes = {
  firstName: PropTypes.string,
  pronouns: PropTypes.arrayOf(PropTypes.string),
  wearsGlasses: PropTypes.string,
  betterWithGlasses: PropTypes.string,
  hasOtherVisualAids: PropTypes.string,
  visualAidsNotes: PropTypes.string,
  formalEvalNotes: PropTypes.string,
  onChangeWearsGlasses: PropTypes.func.isRequired,
  onChangeBetterWithGlasses: PropTypes.func.isRequired,
  onChangeHasOtherVisualAids: PropTypes.func.isRequired,
  onChangeVisualAidsNotes: PropTypes.func.isRequired,
  onChangeFormalEvalNotes: PropTypes.func.isRequired,
  saveFormData: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
};

const defaultProps = {
  firstName: 'the client',
  pronouns: ['they', 'them', 'their'],
  wearsGlasses: '',
  betterWithGlasses: '',
  hasOtherVisualAids: '',
  visualAidsNotes: '',
  formalEvalNotes: ''
};

//content sub-component with unique content for this page
class VisualBackgroundContent extends Component {
  constructor(props){
    super(props);
  }

  //Render the form for asking about background vision.
  //Calls the setter that's passed in by the parent, when a radio button is clicked.
  //That setter changes the state of the vars in the parent, 
  //which triggers a new render of the view with the new var values.
  render(){
    const firstName = this.props.firstName;
    const pronouns = this.props.pronouns;
    const wearsGlasses = this.props.wearsGlasses;
    const betterWithGlasses = this.props.betterWithGlasses;
    const hasOtherVisualAids = this.props.hasOtherVisualAids;
    const visualAidsNotes = this.props.visualAidsNotes;
    const formalEvalNotes = this.props.formalEvalNotes;
    const onChangeWearsGlasses = this.props.onChangeWearsGlasses;
    const onChangeBetterWithGlasses = this.props.onChangeBetterWithGlasses;
    const onChangeHasOtherVisualAids = this.props.onChangeHasOtherVisualAids;
    const onChangeVisualAidsNotes = this.props.onChangeVisualAidsNotes;
    const onChangeFormalEvalNotes = this.props.onChangeFormalEvalNotes;

    //set up button config as an object, then iterate to create markup
    // See https://programmingwithmosh.com/react/handling-form-react/ for starter code
    const wearsLegend = `Does ${firstName} wear glasses (or any corrective lenses)?`;
    const wearsId = "wears-glasses";
    const wearsBtns = [
      {
        value: 'true',
        label: `Yes, ${firstName} wears glasses`,
        moreInfo: ''
      },
      {
        value: 'false',
        label: `No`,
        moreInfo: ''
      }
    ];
    const betterLegend = `Does ${firstName} see the computer screen better with ${pronouns[2]} glasses on, or off?`;
    const betterId = "better-with-glasses";
    const betterBtns = [
      {
        value: 'true',
        label: `Better with glasses on`,
        moreInfo: ''
      },
      {
        value: 'false',
        label: `Better with glasses off`,
        moreInfo: ''
      },
      {
        value: 'not-sure',
        label: `Not sure yet`,
        moreInfo: ''
      }
    ];
    const aidsLegend = `Does ${firstName} have any other visual aids or strategies that help ${pronouns[1]} see better?`;
    const aidsId = "other-visual-aids";
    const aidsBtns = [
      {
        value: 'true',
        label: `Yes, ${firstName} uses other visual aids or strategies`,
        moreInfo: ''
      },
      {
        value: 'false',
        label: `No`,
        moreInfo: ''
      }
    ];
    
    const marginLeft = "ml-sm-3";
    const fadeIn = "aa-fade-in";
    const ANIMATION_ON = AA_PREFERENCES.animationOn;

    return(
    <>
    <Form>
      <FormGroup tag="fieldset" key={wearsId}>
        <legend>{wearsLegend}</legend>
        {wearsBtns.map((btn, index) =>
          <FormGroup className={marginLeft} check key={index}>
            <Input
              id={wearsId+btn.value}
              value={btn.value}
              checked={wearsGlasses===btn.value}
              onChange={onChangeWearsGlasses}
              name={wearsId}
              type="radio"
            />
            {' '}
            <Label for={wearsId+btn.value} check>
              {btn.label}
              {' '}
            </Label>
          </FormGroup>
        )}
      </FormGroup>
      {/* Only show the better-with-glasses question if they actually do wear glasses */}
      {(wearsGlasses==='true') ? 
      // <FormGroup className={ `${fadeIn} ${marginLeft}` } tag="fieldset" key={betterId}>
      <FormGroup className={ ANIMATION_ON && fadeIn } tag="fieldset" key={betterId}>
        <legend>{betterLegend}</legend>
        {betterBtns.map((btn, index) =>
          <FormGroup className={marginLeft} check key={index}>
            <Input
              id={betterId+btn.value}
              value={btn.value}
              checked={betterWithGlasses===btn.value}
              onChange={onChangeBetterWithGlasses}
              name={betterId}
              type="radio"
            />
            {' '}
            <Label for={betterId+btn.value} check>
              {btn.label}
              {' '}
            </Label>
          </FormGroup>
        )}
      </FormGroup> 
      : <span></span>}
      {/* TODO This whole thing could be the InfoMessage component - pass in the T/F value plus the text, and return the markup */}
      {((betterWithGlasses==='true') && (wearsGlasses==='true'))? 
        <p className={fadeIn}>
          <InfoMessage
            text={`Since ${firstName} sees better with glasses on, be sure to keep them on through the rest of the session.`}
          />
        </p> 
        : <span></span>}
      <FormGroup tag="fieldset" key={aidsId}>
        <legend>{aidsLegend}</legend>
        {aidsBtns.map((btn, index) =>
          <FormGroup className={marginLeft} check key={index}>
            <Input
              id={aidsId+btn.value}
              value={btn.value}
              checked={hasOtherVisualAids===btn.value}
              onChange={onChangeHasOtherVisualAids}
              name={aidsId}
              type="radio"
            />
            {' '}
            <Label for={aidsId+btn.value} check>
              {btn.label}
              {' '}
            </Label>
          </FormGroup>
        )}
      </FormGroup>
      {/* Only show the visualAidsNotes question if they said they do have visual aids */}
      {(hasOtherVisualAids==='true') ? 
        <div className={fadeIn}>
        <FormGroup className={marginLeft}>
          <Label for="visual-aids-notes" className="aa-bold-text">
            List those visual aids or strategies here:
          </Label>
          <Input
            id="visual-aids-notes"
            value={visualAidsNotes}
            onChange={onChangeVisualAidsNotes}
            name="visual-aids-notes"
            type="textarea"
            autoFocus={true}
          />
        </FormGroup>
        <p>
          <InfoMessage 
            text={`Try to use ${firstName}'s visual aids during this session, where applicable and available.`}
          />
        </p> 
        </div>
        : <span></span>}
      <FormGroup>
        <Label for="formal-eval" className="aa-bold-text">
          If you have any information from a formal vision assessment, enter key points here:
        </Label>
        <Input
          id="formal-eval"
          value={formalEvalNotes}
          onChange={onChangeFormalEvalNotes}
          name="formal-eval"
          type="textarea"
        />
      </FormGroup>
    </Form>
    </>
  );
}
}

//main component for this page
//basic pattern is to set up the pageData, define the unique content for the page (above),
//then render the page using <Body> component
function EvaluateAbilitiesVisualBackground(props) {
  const firstName = props.firstName;
  const pronouns = props.pronouns;
  const wearsGlasses = props.wearsGlasses;
  const betterWithGlasses = props.betterWithGlasses;
  const hasOtherVisualAids = props.hasOtherVisualAids;
  const visualAidsNotes = props.visualAidsNotes;
  const formalEvalNotes = props.formalEvalNotes;
  const onChangeWearsGlasses = props.onChangeWearsGlasses;
  const onChangeBetterWithGlasses = props.onChangeBetterWithGlasses;
  const onChangeHasOtherVisualAids = props.onChangeHasOtherVisualAids;
  const onChangeVisualAidsNotes = props.onChangeVisualAidsNotes;
  const onChangeFormalEvalNotes = props.onChangeFormalEvalNotes;
  const saveFormData = props.saveFormData;
  const onContinue = props.onContinue;

  console.log("rendering GatherInfoHistoryHasCurrent");

  const pageData = {
    image: "./assets/images/target-icon.jpg", //not yet used
    faIcon: "fa-eye",
    pageSymbolClassName: "aa-section-symbol eval",
    title: `Let's collect some basic info on vision`,
    subTitle: "",
    learnMore: {linkText: "Learn More about basic visual info",
                title: "Basic Visual Info",
                content: VISUAL_BKGND_HELP
                },
    nextRoute: "", //not used since we have an onContinue callback
    sidebar: true
  }

  useEffect(() => {
    console.log('EAVBkgnd mounted');
    return () => {
      console.log('EAVBkgnd unmounting and saving...');
      saveFormData();
    }
  }, [saveFormData])

  return(
    <>
    <Body page={pageData} 
      contentComponent={
        <VisualBackgroundContent 
          firstName={firstName} 
          pronouns={pronouns}
          wearsGlasses={wearsGlasses}
          betterWithGlasses={betterWithGlasses}
          hasOtherVisualAids={hasOtherVisualAids}
          visualAidsNotes={visualAidsNotes}
          formalEvalNotes={formalEvalNotes}
          onChangeWearsGlasses={onChangeWearsGlasses}
          onChangeBetterWithGlasses={onChangeBetterWithGlasses}
          onChangeHasOtherVisualAids={onChangeHasOtherVisualAids}
          onChangeVisualAidsNotes={onChangeVisualAidsNotes}
          onChangeFormalEvalNotes={onChangeFormalEvalNotes}
        />
      }
      onContinue={onContinue} 
    />
    </>
  );
}

EvaluateAbilitiesVisualBackground.propTypes = propTypes;
EvaluateAbilitiesVisualBackground.defaultProps = defaultProps;

export default EvaluateAbilitiesVisualBackground;